import { useQuery } from '@tanstack/react-query';
import type { AderenciaPorDimensao } from '@central/contracts';
import {
  Carregando,
  Contagem,
  derivar,
  Falhou,
  Numero,
  Painel,
  ResumoDoRecorte,
  type ColunaDeTabela,
} from '@/features/relatorios/componentes';
import { useFiltro } from '@/features/relatorios/filtros';
import { GraficoDeAns } from '@/features/relatorios/graficos';
import { chavesDeRelatorio, obterAns } from '@/features/relatorios/relatorios.api';

/**
 * Cumprimento de ANS.
 *
 * As três quebras existem porque respondem a conversas diferentes: SLA é a
 * conversa com o cliente, OLA é a conversa entre equipes e UC é a conversa com
 * o fornecedor. O documento 09 chama esse par de OLA/UC de "o que transforma
 * discussão de SLA em conversa com dado" — e é literalmente isso: sem ele, a
 * reunião termina em "a culpa foi da infra" sem número nenhum.
 */

const COLUNAS: ReadonlyArray<ColunaDeTabela<AderenciaPorDimensao>> = [
  { cabecalho: 'Item', celula: (l) => l.rotulo, csv: (l) => l.rotulo },
  { cabecalho: 'Cumpridos', alinharADireita: true, celula: (l) => l.cumprido, csv: (l) => l.cumprido },
  { cabecalho: 'Em risco', alinharADireita: true, celula: (l) => l.emRisco, csv: (l) => l.emRisco },
  { cabecalho: 'Violados', alinharADireita: true, celula: (l) => l.violado, csv: (l) => l.violado },
  { cabecalho: 'Total', alinharADireita: true, celula: (l) => l.total, csv: (l) => l.total },
  {
    cabecalho: 'Aderência',
    alinharADireita: true,
    // Traço, e não "0%": aderência só existe sobre o que já encerrou, e uma
    // equipe com três compromissos ainda correndo não tem aderência zero.
    celula: (l) => percentual(l.aderencia),
    csv: (l) => percentual(l.aderencia),
  },
];

const percentual = (fracao: number | null) =>
  fracao === null ? '—' : `${(fracao * 100).toFixed(1).replace('.', ',')}%`;

const paraBarra = (linha: AderenciaPorDimensao) => ({
  rotulo: linha.rotulo,
  cumprido: linha.cumprido,
  emRisco: linha.emRisco,
  violado: linha.violado,
});

export function AnsPage() {
  const { consulta } = useFiltro();
  const relatorio = useQuery({
    queryKey: chavesDeRelatorio.de('ans', consulta),
    queryFn: () => obterAns(consulta),
  });

  if (relatorio.isPending) return <Carregando />;
  if (relatorio.isError) {
    return <Falhou erro={relatorio.error} aoTentar={() => void relatorio.refetch()} />;
  }

  const dados = relatorio.data;
  const geral = dados.geral.estado === 'OK' ? dados.geral.valor : null;

  return (
    <div className="animate-slide-up space-y-6">
      <ResumoDoRecorte recorte={dados.recorte} />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
        <Numero
          rotulo="Aderência"
          indicador={derivar(dados.geral, (resumo) => resumo.aderencia, {
            motivo: 'Nenhum compromisso do período já encerrou.',
            falta: 'Ao menos um chamado resolvido ou com prazo vencido.',
          })}
          formatar={(valor) => `${(valor * 100).toFixed(1).replace('.', ',')}%`}
          tom={geral?.aderencia !== null && (geral?.aderencia ?? 0) >= 0.95 ? 'bom' : 'atencao'}
          ajuda="cumpridos ÷ (cumpridos + violados)"
        />
        <Contagem rotulo="Cumpridos" valor={geral?.cumprido ?? 0} tom="bom" />
        <Contagem rotulo="Em andamento" valor={geral?.emAndamento ?? 0} />
        <Contagem rotulo="Em risco" valor={geral?.emRisco ?? 0} tom="atencao" />
        <Contagem rotulo="Violados" valor={geral?.violado ?? 0} tom="ruim" />
        <Contagem
          rotulo="Sem compromisso"
          valor={geral?.semCompromisso ?? 0}
          ajuda="Chamados sem política de ANS aplicável. Não entram na aderência."
        />
      </div>

      {/*
        Estes painéis são de largura inteira até telas muito largas: a tabela
        tem seis colunas, e espremê-la ao lado do gráfico em meia tela deixaria
        "Violados" e "Aderência" fora do campo de visão — justamente as duas que
        alguém abre esta aba para ver.
      */}
      <div className="grid gap-6 2xl:grid-cols-2">
        <Painel
          titulo="SLA por equipe"
          descricao="O acordo com o cliente, olhado pela equipe que atendeu."
          indicador={dados.porEquipe}
          grafico={(linhas) => <GraficoDeAns dados={linhas.map(paraBarra)} />}
          colunas={() => COLUNAS}
          chaveDe={(linha) => linha.chave}
          nome={['relatorio', 'ans', 'sla-por-equipe']}
          recorte={dados.recorte}
        />
        <Painel
          titulo="SLA por organização"
          descricao="A mesma aderência, olhada pelo cliente que a contratou."
          indicador={dados.porOrganizacao}
          grafico={(linhas) => <GraficoDeAns dados={linhas.map(paraBarra)} />}
          colunas={() => COLUNAS}
          chaveDe={(linha) => linha.chave}
          nome={['relatorio', 'ans', 'sla-por-organizacao']}
          recorte={dados.recorte}
        />
        <Painel
          titulo="OLA violado por equipe"
          descricao="O acordo interno que sustenta o SLA. Quando o prazo do cliente estoura, é aqui que costuma estar a causa."
          indicador={dados.olaPorEquipe}
          grafico={(linhas) => <GraficoDeAns dados={linhas.map(paraBarra)} />}
          colunas={() => COLUNAS}
          chaveDe={(linha) => linha.chave}
          nome={['relatorio', 'ans', 'ola-por-equipe']}
          recorte={dados.recorte}
        />
        <Painel
          titulo="UC violado por fornecedor"
          descricao="O contrato de apoio. É o número que se leva para a renovação."
          indicador={dados.ucPorFornecedor}
          grafico={(linhas) => <GraficoDeAns dados={linhas.map(paraBarra)} />}
          colunas={() => COLUNAS}
          chaveDe={(linha) => linha.chave}
          nome={['relatorio', 'ans', 'uc-por-fornecedor']}
          recorte={dados.recorte}
        />
      </div>
    </div>
  );
}
